<script lang="ts">
    import Textfield from '@smui/textfield';
    import TFIcon from '@smui/textfield/icon';
    import Button, {Label} from '@smui/button';
    import Checkbox from '@smui/checkbox';
    import FormField from '@smui/form-field';
    import {Icon} from '@smui/common';

    import CircularProgress from '@smui/circular-progress';
    import {Artist, artists, roseService} from "../rose";
    import Dialog, {Actions, Content, Title} from '@smui/dialog';
    import Autocomplete from "@smui-extra/autocomplete";
    import {Text} from "@smui/list";
    import {navigate} from "svelte-routing";
    import {randomElement, searchCaseAccentInsensitive} from "../utils";
    import CharacterCounter from "@smui/textfield/character-counter";
  import Canvas from '../canvas/Canvas.svelte';
  import { draw } from 'svelte/transition';

    const disabled = true;

    let sketched = false;
    let description = null;
    let email = null;
    let checked = false;

    let loading = false;
    let openDialog = false;
    let message = null;

    $: c_email = /^[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/.test(email);
    $: complete = checked && sketched && description && email && c_email;

    const descriptionExamples = ['Estil cubista i molts colors', 'Un gat sobre una taula', 'Plou i fa sol', 'Abstracta i colorida'];
    const descriptionExample = randomElement(descriptionExamples);

    async function createRose() {
        if (disabled) {
            return;
        }
        if (!checked || !sketched || !description || !email || !c_email) {
            return;
        }
        loading = true;

        let svg = document.querySelector("svg.sketch");
        let canvas = document.getElementById('canvas');
        let ctx = canvas.getContext('2d');
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.width);

        let img = new Image();
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg));

        img.onload = async function () {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(async pngBlob => {
                const file = new File([pngBlob], 'raw_image.png', { type: 'image/png' });
                const result = await roseService.createRose(file, description, email);
                if (result.error) {
                    message = result.message
                    openDialog = true;
                } else {
                    const roseId = result.message;
                    navigate(`/details/${roseId}`);
                }
                loading = false;
            })
        };
    }
</script>

{#if loading}
    <div style="display: flex; justify-content: center">
        <CircularProgress style="height: 32px; width: 32px;" indeterminate/>
    </div>
{:else}
    <div id="form-div">
        <p id="creation-recommendation-texts">
            Aquesta opció ja no està disponible, però encara pots veure totes les roses ja creades.
        </p>
        <p id="creation-recommendation-texts" class:text-disabled={disabled}>
            Amb el cursor de l’ordinador o a la pantalla del dispositiu mòbil, dibuixa el que vulguis en aquest
            espai quadrat i introdueix la descripció que vols associar al teu dibuix. No cal que sigui un
            dibuix gaire elaborat, pot ser un esbós o un traç. La intel·ligència artificial generarà una rosa.
            <br><br>
            Abans de prémer el botó de ‘crear rosa’, pots canviar el gruix del dibuix, esborrar el que has
            fet, tornar a dibuixar i veure una animació dels traços que has dibuixat.
            <br><br>
            La descripció no pot contenir paraules ofensives, humiliants, sexistes, discriminatòries,
            xenòfobes, racistes i/o que atemptin contra la dignitat de la persona.
        </p>
        <Canvas bind:drawing={sketched} disabled={disabled} />

        <div id="description-form-field">
            <Textfield style="width: 100%;" name="description" variant="outlined"
                       required bind:value={description} label="Descripció" input$maxlength={200}
                       input$placeholder="Exemple: &quot;{descriptionExample}&quot;"
                       disabled="{disabled}">
                <CharacterCounter slot="helper">0 / 60</CharacterCounter>
            </Textfield>
        </div>
        <div id="email-form-field">
            <Textfield style="width: 100%;" name="email" variant="outlined" type="email"
                       required bind:value={email} label="Correu electrònic"
                       disabled="{disabled}">
                <TFIcon class="material-icons" slot="leadingIcon">email</TFIcon>
            </Textfield>
        </div>

        <p id="creation-disclaimer-text" class:text-disabled={disabled}>
            <Icon class="material-icons">info</Icon>
            La descripció no pot contenir paraules ofensives, humiliants, sexistes, discriminatòries, xenòfobes,
            racistes i/o que atemptin contra la dignitat de la persona.
        </p>

        <FormField>
            <Checkbox bind:checked />
            <span slot="label" class:text-disabled={disabled}>Dono el meu consentiment a la UPC per al tractament de les dades de caràcter personal.
                Les dades recollides es tractaran tal com es descriu a la <a
                        href="https://www.upc.edu/ca/avis-legal/informacio-i-acces-a-les-dades-personals"
                        target="_blank" class="text-link">taula d'informació i accés a les dades personals</a>.
            </span>
        </FormField>
        <div class="submit-button-div {complete ? '': 'disabled'}">
            <Button type="submit" variant="unelevated" style="margin-top: 10px" on:click={createRose}
                    class="{complete ? '' : 'submit-button-disabled'}" disabled="{disabled}">
                <Label>Crear Rosa</Label>
            </Button>
        </div>
    </div>
{/if}
<Dialog
        bind:open={openDialog}
        aria-labelledby="simple-title"
        aria-describedby="simple-content"
>
    <Title id="simple-title">
        Hi ha hagut un error
    </Title>
    <Content id="simple-content">
        {message}
    </Content>
    <Actions>
        <Button>
            <Label>D'acord</Label>
        </Button>
    </Actions>
</Dialog>

<style>
    .text-disabled {
        opacity: 50%;
    }

    #form-div {
        margin: 1vh 0;
        display: flex;
        gap: 15px;
        flex-direction: column;
        align-items: stretch;
    }

    #creation-disclaimer-text {
        margin-top: 0;
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.875rem;
    }

    :global(#creation-disclaimer-text > i) {
        transform: rotate(180deg);
    }

    :global(.submit-button-div) {
        align-self: flex-start;
    }

    :global(.submit-button-div.disabled) {
        cursor: not-allowed;
    }

    :global(.submit-button-disabled) {
        pointer-events: none;
    }

</style>